import React, { useState, useEffect, useRef } from 'react';
import {
  getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, 
  onSnapshot, query, orderBy, Timestamp, where, getDocs, writeBatch
} from 'firebase/firestore';
import { Offcanvas, Button, Form, InputGroup, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import { FaTrashAlt, FaHistory, FaPlus, FaUndo, FaStar, FaPencilAlt, FaTimes } from 'react-icons/fa';

const db = getFirestore();

const VideoAdminConsole = ({ isOpen, onClose }) => {
  const [videos, setVideos] = useState([]);
  const [historyResults, setHistoryResults] = useState({});
  const [viewMode, setViewMode] = useState('active'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [newVideo, setNewVideo] = useState({ 
    title: '', videoId: '', voteStart: 10, voteEnd: 40, voteOptions: 'Yes, No', isMulti: false, description: '' 
  });

  const labelRef = useRef(null);
  const descRef = useRef(null);
  const formTopRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, "videos"), orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const vids = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setVideos(vids);
      
      const tallies = {};
      for (const v of vids) {
        const voteQ = query(collection(db, "videoVotes"), where("videoId", "==", v.videoId));
        const voteSnap = await getDocs(voteQ);
        const counts = {};
        voteSnap.forEach(d => {
          const val = d.data().vote;
          const choices = Array.isArray(val) ? val : [val];
          choices.forEach(c => { counts[c] = (counts[c] || 0) + 1; });
        });
        tallies[v.videoId] = { counts, total: voteSnap.size };
      }
      setHistoryResults(tallies);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    [labelRef, descRef].forEach(ref => {
      if (ref.current) {
        ref.current.style.height = "auto";
        ref.current.style.height = ref.current.scrollHeight + "px";
      }
    });
  }, [newVideo.voteOptions, newVideo.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const opts = newVideo.voteOptions.split(',').map(o => o.trim()).filter(o => o !== '');
    const data = { ...newVideo, voteOptions: opts.length > 0 ? opts : ['Yes', 'No'] };

    if (editingId) {
      await updateDoc(doc(db, "videos", editingId), data);
      setEditingId(null);
    } else {
      await addDoc(collection(db, "videos"), { ...data, isFeatured: false, isArchived: false, timestamp: Timestamp.now() });
    }
    setNewVideo({ title: '', videoId: '', voteStart: 10, voteEnd: 40, voteOptions: 'Yes, No', isMulti: false, description: '' });
  };

  const handleToggleFeatured = async (id, currentStatus) => {
    const batch = writeBatch(db);
    if (!currentStatus) {
      const activeQuery = query(collection(db, "videos"), where("isFeatured", "==", true));
      const activeDocs = await getDocs(activeQuery);
      activeDocs.forEach((d) => batch.update(doc(db, "videos", d.id), { isFeatured: false }));
    }
    batch.update(doc(db, "videos", id), { isFeatured: !currentStatus, isArchived: false });
    await batch.commit();
  };

  const handleResetVotes = async (vid) => {
    if (window.confirm(`Reset all vote data for "${vid.title}"?`)) {
      const q = query(collection(db, "videoVotes"), where("videoId", "==", vid.videoId));
      const snap = await getDocs(q);
      const batch = writeBatch(db);
      snap.forEach(d => batch.delete(d.ref));
      await batch.commit();
      
      // Update local state immediately to refresh the list UI
      setHistoryResults(prev => ({
        ...prev,
        [vid.videoId]: { counts: {}, total: 0 }
      }));
    }
  };

  const handleEditClick = (vid) => {
    setEditingId(vid.id);
    setNewVideo({ 
        title: vid.title, 
        videoId: vid.videoId, 
        voteStart: vid.voteStart, 
        voteEnd: vid.voteEnd, 
        voteOptions: vid.voteOptions?.join(', '), 
        isMulti: !!vid.isMulti, 
        description: vid.description || '' 
    });
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredVideos = videos.filter(v => 
    v.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (viewMode === 'active' ? !v.isArchived : v.isArchived)
  );

  const renderTooltip = (props, text) => (<Tooltip id="button-tooltip" {...props}>{text}</Tooltip>);

  return (
    <Offcanvas show={isOpen} onHide={onClose} placement="end" className="bg-dark text-white" style={{ width: '100%', maxWidth: '550px' }}>
      <Offcanvas.Header closeButton closeVariant="white" className="border-bottom border-secondary border-opacity-25">
        <Offcanvas.Title><h3 className="modified xTitle animated rollIn m-0">Votez</h3></Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0 d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>
        <div className="p-3 border-bottom border-secondary border-opacity-10">
          <InputGroup className="mb-2">
            <Form.Control placeholder="🔎 Search video library..." className="glass-search text-white border-secondary shadow-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </InputGroup>
          <div className="d-flex gap-2">
            <Button variant={viewMode === 'active' ? "info" : "outline-info"} size="sm" className="fw-bold flex-grow-1" onClick={() => setViewMode('active')}><FaPlus className="me-1"/> DEPLOY</Button>
            <Button variant={viewMode === 'archived' ? "info" : "outline-info"} size="sm" className="fw-bold flex-grow-1" onClick={() => setViewMode('archived')}><FaHistory className="me-1"/> ARCHIVE</Button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto p-3">
          <div ref={formTopRef} />

          {viewMode === 'active' && (
            <div className={`doc-card p-3 mb-4 rounded border ${editingId ? 'border-warning' : 'border-info'} bg-opacity-10 shadow-sm`}>
              <div className="d-flex justify-content-between mb-3 align-items-center">
                 <h6 className={`${editingId ? 'text-warning' : 'text-info'} fw-bold smallest text-uppercase m-0`}>{editingId ? 'Update Entry' : 'Add New Content'}</h6>
                 {editingId && <Button variant="link" className="text-danger p-0" onClick={() => { setEditingId(null); setNewVideo({ title: '', videoId: '', voteStart: 10, voteEnd: 40, voteOptions: 'Yes, No', isMulti: false, description: '' }); }}><FaTimes /></Button>}
              </div>
              <Form onSubmit={handleSubmit}>
                <Form.Control size="sm" className="glass-input" placeholder="Title" value={newVideo.title} onChange={e => setNewVideo({...newVideo, title: e.target.value})} />
                <Form.Control size="sm" className="glass-input" placeholder="YouTube ID" value={newVideo.videoId} onChange={e => setNewVideo({...newVideo, videoId: e.target.value})} />
                
                <Form.Label className="smallest text-white-50 mb-1" style={{ fontSize: '10px' }}>BUTTON LABELS (COMMA SEPARATED)</Form.Label>
                <Form.Control as="textarea" ref={labelRef} size="sm" className="glass-input overflow-hidden" style={{ resize: 'none', minHeight: '38px' }} placeholder="e.g. Yes, No, Maybe" value={newVideo.voteOptions} onChange={e => setNewVideo({...newVideo, voteOptions: e.target.value})} />

                <Form.Label className="smallest text-white-50 mb-1" style={{ fontSize: '10px' }}>DESCRIPTION / HELP DETAILS</Form.Label>
                <Form.Control as="textarea" ref={descRef} size="sm" className="glass-input overflow-hidden" style={{ resize: 'none', minHeight: '38px' }} placeholder="Provide context for the voters..." value={newVideo.description} onChange={e => setNewVideo({...newVideo, description: e.target.value})} />

                <Form.Check type="checkbox" label="Allow Multiple Selections" className="smallest mb-2 text-info fw-bold" checked={newVideo.isMulti} onChange={e => setNewVideo({...newVideo, isMulti: e.target.checked})} />
                
                <div className="d-flex gap-2 mb-3">
                  <div className="flex-grow-1">
                    <Form.Label className="smallest text-white-50 mb-1" style={{ fontSize: '10px' }}>START (SEC)</Form.Label>
                    <Form.Control size="sm" type="number" className="glass-input border-secondary" value={newVideo.voteStart} onChange={e => setNewVideo({...newVideo, voteStart: Number(e.target.value)})} />
                  </div>
                  <div className="flex-grow-1">
                    <Form.Label className="smallest text-white-50 mb-1" style={{ fontSize: '10px' }}>END (SEC)</Form.Label>
                    <Form.Control size="sm" type="number" className="glass-input text-white border-secondary" value={newVideo.voteEnd} onChange={e => setNewVideo({...newVideo, voteEnd: Number(e.target.value)})} />
                  </div>
                </div>
                
                <Button type="submit" variant={editingId ? "warning" : "info"} size="sm" className="w-100 fw-bold">{editingId ? 'SAVE CHANGES' : 'DEPLOY VIDEO'}</Button>
              </Form>
            </div>
          )}

          <div className="pb-5">
            {filteredVideos.map(vid => {
              const stats = historyResults[vid.videoId] || { counts: {}, total: 0 };
              return (
                <div key={vid.id} className="doc-card p-3 mb-2 rounded border border-secondary bg-dark bg-opacity-50 shadow-sm">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1 pe-2">
                      <div className="text-info fw-bold small mb-1">{vid.title}</div>
                      <div className="smallest text-white-50 mb-2">{vid.isMulti ? 'MULTI' : 'SINGLE'} CHOICE | ID: {vid.videoId}</div>
                      <div className="d-flex flex-wrap gap-1">
                        {Object.entries(stats.counts).map(([l, c]) => (
                          <Badge key={l} bg="info" className="bg-opacity-25 text-info smallest border border-info border-opacity-25">{l}: {c}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2 ms-2">
                      <OverlayTrigger placement="left" overlay={(p) => renderTooltip(p, 'Edit')}>
                          <Button variant="outline-light" size="sm" className="centerCircle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', borderRadius: '50%', borderColor: 'rgba(255,255,255,0.2)' }} onClick={() => handleEditClick(vid)}><FaPencilAlt size={11}/></Button>
                      </OverlayTrigger>
                      <OverlayTrigger placement="left" overlay={(p) => renderTooltip(p, 'Feature')}>
                          <Button variant={vid.isFeatured ? "info" : "outline-secondary"} className="centerCircle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', borderRadius: '50%' }} onClick={() => handleToggleFeatured(vid.id, vid.isFeatured)}><FaStar size={12}/></Button>
                      </OverlayTrigger>
                      <OverlayTrigger placement="left" overlay={(p) => renderTooltip(p, 'Reset Votes')}>
                          <Button variant="outline-warning" className="centerCircle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', borderRadius: '50%' }} onClick={() => handleResetVotes(vid)}><FaUndo size={11}/></Button>
                      </OverlayTrigger>
                      {vid.isArchived && <Button variant="danger" className="centerCircle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', borderRadius: '50%' }} onClick={() => deleteDoc(doc(db, "videos", vid.id))}><FaTrashAlt size={11}/></Button>}
                    </div>
                  </div>
                  <div className="mt-3 d-flex gap-3 border-top border-secondary border-opacity-25 pt-2">
                    <span className="smallest text-info fw-bold text-uppercase" style={{ cursor: 'pointer' }} onClick={() => updateDoc(doc(db, "videos", vid.id), { isArchived: !vid.isArchived, isFeatured: false })}>
                      {vid.isArchived ? 'Restore to Active' : 'Archive Video'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default VideoAdminConsole;